import { MIN_TIER, REPUTATION_PATH_LIST } from "@personnel/setting";
import type { ReputationPathId } from "@personnel/setting";

interface ReputationRowProps {
  renown: number;
  path: ReputationPathId | null;
  tier: number;
  reputation: string | null;
  recommended: number;
  onRenownChange: (renown: number) => void;
  onPathChange: (path: ReputationPathId | null) => void;
}

export function ReputationRow({
  renown,
  path,
  tier,
  reputation,
  recommended,
  onRenownChange,
  onPathChange,
}: ReputationRowProps) {
  const pathUsable = tier > MIN_TIER;

  return (
    <div className="flex flex-col gap-4 rounded-xl border border-void-800 bg-void-900/40 px-4 py-4">
      <div className="flex flex-wrap items-end gap-4">
        <label className="flex flex-col gap-1.5">
          <span className="text-xs tracking-wide text-void-400 uppercase">Renown</span>
          <input
            type="number"
            min={0}
            value={renown}
            onChange={(event) => {
              const next = Number.parseInt(event.target.value, 10);
              onRenownChange(Number.isNaN(next) ? 0 : Math.max(0, next));
            }}
            className="w-24 rounded-md border border-void-800 bg-void-900 px-3 py-2 tabular-nums text-void-200 focus:border-leviathan-600 focus:outline-none"
          />
        </label>

        {/* One-shot, never automatic -- it must not fight a manual entry as Cost moves. */}
        <button
          type="button"
          onClick={() => onRenownChange(recommended)}
          disabled={renown === recommended}
          className="rounded-md border border-void-800 px-3 py-2 text-xs text-void-400 transition-colors hover:border-leviathan-600 hover:text-leviathan-500 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Use recommended ({recommended})
        </button>

        <div className="ml-auto text-right">
          <div className="text-xs tracking-wide text-void-400 uppercase">Tier {tier}</div>
          <div className="font-display text-lg font-semibold text-leviathan-500">
            {reputation ?? "—"}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-xs tracking-wide text-void-400 uppercase">
          Reputation Path
          {!pathUsable && <span className="ml-2 normal-case text-void-700">chosen at Tier 2</span>}
        </span>
        <div className="grid grid-cols-3 gap-2">
          {REPUTATION_PATH_LIST.map((def) => {
            const selected = path === def.id;
            return (
              <button
                key={def.id}
                type="button"
                disabled={!pathUsable}
                onClick={() => onPathChange(selected ? null : def.id)}
                className={`rounded-md border px-3 py-2 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                  selected
                    ? "border-leviathan-600 bg-leviathan-600/10 text-leviathan-500"
                    : "border-void-800 text-void-400 hover:border-leviathan-600 hover:text-leviathan-500"
                }`}
              >
                {def.name}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
